import { Box, Button, PopperProps, Stack, Tooltip } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { SaveSearchFilters } from "../../../App/Components/Filters/SaveSearchFilters";
import { useSavedCampaignsSearchesQuery } from "../../../App/Queries/FilterQueries";
import { ISavedSearch } from "../../../App/Typings/SearchFilters";
import { CandidatesCapturedSavedFilters } from "./CandidatesCapturedSavedFilters";
import { Rule } from "../../../App/Typings/DynamicGroupRules";

interface ISearchFiltersProps {
  rules: Rule[];
  handleSetRules: (rules: Rule[]) => void;
  isLoading: boolean;
  id?: string;
  campaignId?: string;
}

export default function CandidatesCapturedFilters({
  rules,
  handleSetRules,
  isLoading,
  id: htmlId = "campaign-search-filters",
  campaignId = "",
}: ISearchFiltersProps) {
  const [currentFilter, setCurrentFilter] = useState<ISavedSearch | null>(null);
  const [savedDialogOpen, setSavedDialogOpen] = useState<boolean>(false);
  const [saveNewFilterDialogOpen, setSaveNewFilterDialogOpen] = useState<boolean>(false);
  const { data: savedSearchFilters = [] } = useSavedCampaignsSearchesQuery(campaignId);
  const filterQueryString = JSON.stringify(rules);
  const validRules = rules.filter((r) => !!r.filter.type && !!r.filter.value);
  const isLoadDisabled = isLoading || !savedSearchFilters || savedSearchFilters.length <= 0;
  let loadDisabledMessage = isLoading ? "Loading..." : "";
  if (!savedSearchFilters || savedSearchFilters.length <= 0) {
    loadDisabledMessage = "No saved filters available";
  }
  const currentFilterMatchesRules = compareSavedFilterWithCurrentRules(currentFilter, rules);
  const isSaveDisabled =
    isLoading || !filterQueryString || validRules.length === 0 || currentFilterMatchesRules;
  let saveDisabledMessage = isLoading ? "Loading..." : "";
  if (!filterQueryString || validRules.length === 0) {
    saveDisabledMessage = "No filters selected";
  } else if (currentFilterMatchesRules) {
    saveDisabledMessage = "Filter already saved";
  }

  const handleUpdateCurrentFilter = useCallback(() => {
    const matchingFilter = savedSearchFilters.find((f) => f.searchString === filterQueryString);
    if (matchingFilter) {
      setCurrentFilter(matchingFilter);
    }
  }, [filterQueryString, savedSearchFilters]);

  useEffect(() => {
    if (savedSearchFilters.length > 0) {
      handleUpdateCurrentFilter();
    }
  }, [handleUpdateCurrentFilter, savedSearchFilters]);

  return (
    <>
      <Stack direction="row" alignItems="center" data-test={"filter-menu-" + htmlId} gap={0.5}>
        <Tooltip
          data-test="filter-button-load-tooltip"
          title={isLoadDisabled ? loadDisabledMessage : "Load saved search filter"}
          arrow
        >
          <Box>
            <Button
              aria-label="Load saved search filter"
              type="button"
              onClick={() => setSavedDialogOpen(true)}
              variant="text"
              data-test="filter-button-load-filters"
              disabled={isLoadDisabled}
            >
              load
            </Button>
          </Box>
        </Tooltip>
        |
        <Tooltip
          title={isSaveDisabled ? saveDisabledMessage : "Save search filter"}
          PopperProps={
            {
              "data-test": "filter-button-save-tooltip",
            } as Partial<PopperProps>
          }
          arrow
        >
          <Box>
            <Button
              aria-label="Save search filter"
              disabled={isSaveDisabled}
              onClick={() => setSaveNewFilterDialogOpen(true)}
              type="button"
              variant="text"
              data-test="filter-button-save-filters"
            >
              save
            </Button>
          </Box>
        </Tooltip>
      </Stack>
      <CandidatesCapturedSavedFilters
        handleSetRules={handleSetRules}
        setCurrentFilter={setCurrentFilter}
        dialogOpen={savedDialogOpen}
        handleDialogClose={() => setSavedDialogOpen(false)}
        savedSearchFilters={savedSearchFilters}
      />
      <SaveSearchFilters
        queryString={filterQueryString ?? ""}
        dialogOpen={saveNewFilterDialogOpen}
        handleDialogClose={() => setSaveNewFilterDialogOpen(false)}
        campaignId={campaignId}
        onSaveCallback={handleUpdateCurrentFilter}
      />
    </>
  );
}

const compareSavedFilterWithCurrentRules = (
  savedFilter: ISavedSearch | null = null,
  currentRules: Rule[],
) => {
  return savedFilter ? savedFilter.searchString === JSON.stringify(currentRules) : false;
};
